import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Container, Typography, ButtonBase } from "@mui/material";
import { gsap } from "gsap";
import SectionTitle from "../components/SectionTitle";
import VideoModal from "../components/VideoModal";
import mockVideos from "../data/mockVideos";

export default function Videos() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const brandColor = "var(--verdeClaro)";
  const videos = useMemo(() => mockVideos.slice(0, 9), []);

  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const gridRef = useRef(null);

  function handleOpen(v) {
    setSelected(v);
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
    setSelected(null);
  }

  useEffect(() => {
    const sectionEl = sectionRef.current;
    const titleEl = titleRef.current;
    const gridEl = gridRef.current;
    if (!sectionEl || !gridEl) return;

    const cards = gridEl.querySelectorAll("[data-card='true']");

    gsap.set([titleEl, cards], { opacity: 0, y: 30 });

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

        if (titleEl) {
          tl.to(titleEl, { opacity: 1, y: 0, duration: 0.7 });
        }

        tl.to(
          cards,
          {
            opacity: 1,
            y: 0,
            duration: 0.55,
            stagger: 0.06,
          },
          titleEl ? "-=0.25" : 0
        );

        io.disconnect();
      },
      {
        threshold: 0.18,
        rootMargin: "0px 0px -10% 0px",
      }
    );

    io.observe(sectionEl);

    return () => io.disconnect();
  }, []);

  return (
    <Box
      id="videos"
      ref={sectionRef}
      sx={{
        py: { xs: 0, md: 0 },
        bgcolor: "var(--preto)",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 2, md: 3 },
          maxWidth: 1600,
          mx: "auto",
        }}
      >
        <Box
          ref={titleRef}
          sx={{
            mb: 5,
            maxWidth: { xs: "100%", md: 980 },
          }}
        >
          <SectionTitle
            eyebrow="portfólio"
            title="Projetos Recentes"
            subtitle="Passe o mouse para detalhes. Clique para abrir o case."
          />
        </Box>

        <Box
          sx={{
            borderRadius: 0,
            overflow: "hidden",
          }}
        >
          <Box
            ref={gridRef}
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 0,
            }}
          >
            {videos.map((v) => (
              <ButtonBase
                key={v.id}
                data-card="true"
                onClick={() => handleOpen(v)}
                sx={{
                  width: "100%",
                  display: "block",
                  position: "relative",
                  aspectRatio: "16 / 9",
                  overflow: "hidden",
                  lineHeight: 0,
                  fontSize: 0,

                  "&:hover .bg-image": {
                    filter: "grayscale(100%) blur(2.2px)",
                    transform: "scale(1.06)",
                    opacity: 0.85,
                  },
                  "&:hover .content-overlay": {
                    opacity: 1,
                  },
                  "&:hover .accent-line": {
                    width: "62px",
                    opacity: 1,
                  },
                  "&:hover .accent-corner": {
                    opacity: 1,
                    transform: "scale(1)",
                  },
                  "&:hover .wipe": {
                    transform: "translateX(140%) rotate(18deg)",
                    opacity: 1,
                  },
                }}
              >
                <Box
                  component="img"
                  src={v.thumb}
                  alt={v.title}
                  className="bg-image"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transform: "scale(1.02)",
                    transition:
                      "filter .25s ease, transform .40s cubic-bezier(0.16, 1, 0.3, 1), opacity .25s ease",
                    filter: "grayscale(0%) blur(0px)",
                    opacity: 1,
                    display: "block",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.55))",
                    opacity: 0.35,
                    transition: "opacity .25s ease",
                    zIndex: 1,
                  }}
                />

                <Box
                  className="wipe"
                  sx={{
                    position: "absolute",
                    inset: "-30% -20%",
                    pointerEvents: "none",
                    zIndex: 2,
                    opacity: 0,
                    transform: "translateX(-160%) rotate(18deg)",
                    transition:
                      "transform .55s cubic-bezier(0.16, 1, 0.3, 1), opacity .2s ease",
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(172,201,14,0.0) 35%, rgba(172,201,14,0.18) 50%, rgba(172,201,14,0.0) 65%, transparent 100%)",
                  }}
                />

                <Box
                  className="accent-corner"
                  sx={{
                    position: "absolute",
                    top: 14,
                    left: 14,
                    width: 10,
                    height: 10,
                    borderRadius: 999,
                    bgcolor: brandColor,
                    opacity: 0,
                    transform: "scale(0.85)",
                    transition: "all .28s cubic-bezier(0.16, 1, 0.3, 1)",
                    boxShadow: "0 0 16px rgba(172,201,14,0.35)",
                    zIndex: 3,
                  }}
                />

                <Box
                  className="content-overlay"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0,0,0,0.52)",
                    opacity: 0,
                    transition: "opacity .22s ease",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                    zIndex: 4,
                  }}
                >
                  <Typography
                    sx={{
                      color: "var(--branco)",
                      fontWeight: 1000,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      textAlign: "center",
                      mb: 1,
                      fontSize: { xs: "1.05rem", md: "1.12rem" },
                      textShadow: "0 6px 22px rgba(0,0,0,0.55)",
                    }}
                  >
                    {v.title}
                  </Typography>

                  <Box
                    className="accent-line"
                    sx={{
                      height: 3,
                      width: 0,
                      bgcolor: brandColor,
                      mb: 1.5,
                      borderRadius: 2,
                      boxShadow: "0 0 14px rgba(172,201,14,0.35)",
                      transition: "all .30s cubic-bezier(0.16, 1, 0.3, 1)",
                      opacity: 0,
                    }}
                  />

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.88)",
                      textAlign: "center",
                      fontWeight: 500,
                      fontSize: "0.88rem",
                      maxWidth: 420,
                    }}
                  >
                    {v.subtitle || "Ver projeto"}
                  </Typography>
                </Box>
              </ButtonBase>
            ))}
          </Box>
        </Box>
      </Container>

      <VideoModal open={open} onClose={handleClose} video={selected} />
    </Box>
  );
}